import { useEffect, useMemo, useRef, useState } from 'react';
import { Text, VStack } from 'ui_2';
import { BackgroundLowRes } from './BackgroundLowRes';
import { SquashLowRes } from './SquashLowRes';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  PaginationLink,
  PaginationEllipsis,
} from 'ui_2';
import dayjs from 'dayjs';
import { useSquashGameState } from '@src/providers/squashGameState';
import { SquashMoodView } from './SquashMood';

export const Diary = () => {
  const { activeSquash, squashHistoryDesc } = useSquashGameState();
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(
    Math.min(30, squashHistoryDesc?.length ?? 0)
  );

  const targetRef = useRef<HTMLDivElement>(null);

  const displayedSquashes = useMemo(
    () => (squashHistoryDesc ?? []).slice(startIndex, endIndex),
    [
      JSON.stringify(activeSquash),
      JSON.stringify(squashHistoryDesc),
      startIndex,
      endIndex,
    ]
  );

  useEffect(() => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [startIndex, endIndex]);

  return (
    <VStack className='pb-[100px]'>
      <VStack className='max-h-[5000px] w-full overflow-y-auto'>
        <div className='h-0 -translate-y-4' ref={targetRef} />
        <div className='mb-2 text-[12px] font-normal'>
          {displayedSquashes?.[0]?.time
            ? dayjs(displayedSquashes[0].time).format('MMMM YYYY').toUpperCase()
            : ''}
        </div>
        <div>
          <div className='grid grid-cols-4 gap-4 sm:grid-cols-5'>
            {displayedSquashes.map((entry, index) => (
              <VStack key={index} className='items-center'>
                <Text>{dayjs(entry.time).format('M/D')}</Text>
                <div key={index} className='relative h-[70px] w-[70px]'>
                  <BackgroundLowRes level={entry.onChainLevel} />
                  <SquashLowRes level={entry.onChainLevel} mood={entry.mood} />
                </div>
                <Text className='text-center' font='sm'>
                  {entry.kg}kg
                  <br />
                  <SquashMoodView mood={entry.mood} size='small' />
                </Text>
              </VStack>
            ))}
          </div>
        </div>
      </VStack>
      <Pagination className='absolute bottom-0 -ml-4 h-10 w-full bg-squashBg py-6 shadow-[0_-4px_4px_0_rgba(0,0,0,0.1)]'>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              className={`${
                startIndex === 0 ? 'cursor-not-allowed opacity-50' : ''
              }`}
              onClick={() => {
                if (startIndex > 0) {
                  setStartIndex(startIndex - 30);
                  setEndIndex(endIndex - 30);
                }
              }}
            />
          </PaginationItem>

          {[...Array(Math.ceil((squashHistoryDesc?.length ?? 0) / 30))].map(
            (_, idx) => {
              const pageNumber = idx + 1;
              const currentPage = Math.floor(startIndex / 30) + 1;

              // Always show first page, current page, last page, and pages around current page
              if (
                pageNumber === 1 ||
                pageNumber ===
                  Math.ceil((squashHistoryDesc?.length ?? 0) / 30) ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <PaginationItem key={idx}>
                    <PaginationLink
                      onClick={() => {
                        setStartIndex((pageNumber - 1) * 30);
                        setEndIndex(pageNumber * 30);
                      }}
                      isActive={currentPage === pageNumber}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              }

              // Show ellipsis for breaks in number sequence
              if (
                pageNumber === 2 ||
                pageNumber ===
                  Math.ceil((squashHistoryDesc?.length ?? 0) / 30) - 1
              ) {
                return <PaginationEllipsis key={idx} />;
              }

              return null;
            }
          )}

          <PaginationItem>
            <PaginationNext
              className={`${
                endIndex >= (squashHistoryDesc?.length ?? 0)
                  ? 'cursor-not-allowed opacity-50'
                  : ''
              }`}
              onClick={() => {
                if (endIndex < (squashHistoryDesc?.length ?? 0)) {
                  setStartIndex(startIndex + 30);
                  setEndIndex(endIndex + 30);
                }
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </VStack>
  );
};
